/**
 * BusinessGates — Corp / LLC / LLP / Partnership / DBA / SoleProp.
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (BUSINESS_FIELDS + DBA branch)
 *   - _validateEntityRestrictions (no-op for generic business)
 *   - _validateRelationships (BIZ_AUTH_SIGNER, PARTNER_MIN_SIGNERS, DBA_BUSINESS_LINK)
 *   - _validateDocumentCollection (BO form, banking res, operating agreement)
 *
 * Plus v3 additions: Beneficial Ownership (BO) matrix gate, SOS verification.
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildOfacGate,
  buildRequiredFieldGates,
  getByRole,
  getPrimary,
  type FieldSpec,
} from '../ProceduralGates';

const BUSINESS_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'name2', label: 'Entity name' },
  { field: 'taxId', label: 'EIN' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

const DBA_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'firstName', label: 'Owner first name' },
  { field: 'lastName', label: 'Owner last name' },
  { field: 'taxId', label: 'EIN or SSN' },
  { field: 'name2', label: 'DBA / trade name' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

export function runBusinessGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates;

  const et = (cart.entityType as string) ?? '';
  const isDBA = et === 'DBA';

  // Required fields
  gates.push(...buildRequiredFieldGates(cart, primary, isDBA ? DBA_FIELDS : BUSINESS_FIELDS));

  // Authorized signer relationship — at least 1 (SPFx BIZ_AUTH_SIGNER).
  const authSigners = getByRole(cart, 'signer');
  const joints = getByRole(cart, 'joint');
  if (authSigners.length + joints.length === 0) {
    gates.push({
      gateCode: 'BIZ_AUTH_SIGNER',
      entityType: et,
      category: 'relationship',
      status: 'fail',
      label: 'Authorized signer required',
      reason: 'Business accounts require at least 1 authorized signer.',
      remediationAction: { label: 'Add signer', deepLinkStepId: 'people' },
    });
  }

  // Partnership — at least 2 signers (SPFx PARTNER_MIN_SIGNERS)
  if (
    et === 'Partnership' ||
    et === 'LimitedPartnership' ||
    et === 'GeneralPartnership' ||
    et === 'LLP' ||
    et === 'LLLP'
  ) {
    if (authSigners.length + joints.length < 2) {
      gates.push({
        gateCode: 'PARTNER_MIN_SIGNERS',
        entityType: et,
        category: 'relationship',
        status: 'fail',
        label: 'Partnership requires 2+ authorized signers',
        reason: 'Partnership accounts require at least 2 authorized signers.',
        remediationAction: { label: 'Add signers', deepLinkStepId: 'people' },
      });
    }
  }

  // DBA linkage check (SPFx DBA_BUSINESS_LINK)
  if (isDBA && !primary.name2) {
    gates.push({
      gateCode: 'DBA_BUSINESS_LINK',
      entityType: et,
      category: 'relationship',
      status: 'fail',
      label: 'DBA trade name required',
      reason: 'DBA accounts require the business entity name (DBA name) to be specified.',
      remediationAction: { label: 'Set DBA name', deepLinkStepId: 'people', fieldId: 'name2' },
    });
  }

  // Beneficial Ownership (BO) matrix — SPFx WorkflowEngine BO gate.
  const owners = cart.beneficialOwners ?? [];
  const owner25 = owners.filter((o) => o.percent >= 25);
  const controller = owners.find((o) => o.isController);
  if (!controller) {
    gates.push({
      gateCode: 'BO',
      entityType: et,
      category: 'compliance',
      status: 'fail',
      label: 'Beneficial Ownership (control person required)',
      reason: 'FinCEN CDD rule — a control person must be identified.',
      remediationAction: { label: 'Add control person', deepLinkStepId: 'compliance' },
    });
  } else if (owner25.length === 0) {
    gates.push({
      gateCode: 'BO_CONTROLLER_ONLY',
      entityType: et,
      category: 'compliance',
      status: 'pass',
      label: 'Beneficial Ownership (control person only)',
      reason: 'Control person identified; no 25%+ owners on record.',
    });
  } else {
    gates.push({
      gateCode: 'BO',
      entityType: et,
      category: 'compliance',
      status: 'pass',
      label: 'Beneficial Ownership (25%+ owners + control person)',
      reason: `${owner25.length} owner(s) ≥ 25% plus 1 control person identified.`,
    });
  }

  // Secretary of State verification (SPFx SOS_VERIFY) — pending until
  // SOSVerifyService ports. TODO(v3.1).
  if (et === 'Corporation' || et === 'LLC' || et === 'LLP' || et === 'LLLP') {
    gates.push({
      gateCode: 'SOS_VERIFY',
      entityType: et,
      category: 'compliance',
      status: 'warn',
      label: 'Secretary of State verification',
      reason: 'Pending: SOS lookup wires up with business-compliance module.',
    });
  }

  // CIP applies to the control person / authorized signer (not the entity)
  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  // W-9 always expected for domestic business; W-8BEN-E for foreign.
  gates.push({
    gateCode: 'W9_COLLECTED',
    entityType: et,
    category: 'documentCollection',
    status: 'warn',
    label: 'W-9 collected',
    reason: 'Pending (DocumentGeneratorService): generate + collect W-9 for entity.',
  });

  return gates;
}
